import { Injectable } from '@angular/core';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { SignupFormHelper } from './sign-up.form-helper';
import { CreateAccountModel } from '../../global-data-models/create-account.model';
import { SignupService } from '../../global-services/signup.service';
import { FirebaseService } from '../../global-services/firebase.service';
import { Router } from '@angular/router';
import { MeService } from '../../global-services/me.service';
import { ConsentData } from '../../global-components/global-consent-form/global-consent-form.component';
import { RecaptchaService } from '../../global-services/recaptcha.service';

export abstract class SignUpState {
  constructor(public form: FormGroup) { }
}
export class DisplayState extends SignUpState {
  static blankDisplayState() {
    return new DisplayState(new FormGroup({}));
  }
}

@Injectable({ providedIn: 'root' })
export class SignUpFacade {
  private stateSubject: BehaviorSubject<SignUpState>;
  state$;
  public form: FormGroup;
  private currentInviteToken?: string | null = null;

  constructor(
    private message: GlobalMessageService,
    private loading: GlobalLoadingService,
    private recaptchaService: RecaptchaService,
    private signupService: SignupService,
    private firebaseService: FirebaseService,
    private userService: MeService,
    private router: Router
  ) {
    this.form = SignupFormHelper.getFormGroup();
    this.stateSubject = new BehaviorSubject<SignUpState>(
      new DisplayState(this.form)
    );
    this.state$ = this.stateSubject.asObservable();
  }

  async begin(email?: string | null, _inviteToken?: string) {
    this.stateSubject.next(new DisplayState(this.form));
    this.form.setErrors(null);

    if (email) {
      this.form.patchValue({ email });
      this.form.get('email')?.disable();
    }
    if (_inviteToken) {
      this.currentInviteToken = _inviteToken;
    }
  }

  async signUpPressed(consentData: ConsentData) {
    try {
      this.loading.show();
      this.form.setErrors(null);

      const token = await this.recaptchaService.executeRecaptcha('signup');
      await this._doSignUp(token, consentData);
    } catch (error) {
      const errorObj = error as any;
      const message =
        errorObj.error?.message || errorObj.message || 'unknown error';
      this.message.showError('Sign Up Failed', message);
    } finally {
      this.loading.hide();
    }
  }

  private async _doSignUp(recaptchaToken: string | null, consentData: ConsentData) {
    const formValues = this.form.getRawValue();

    const account: CreateAccountModel = {
      title: formValues.title,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email.toLowerCase(),
      password: formValues.password,
      recaptchaToken: recaptchaToken ?? null,
      consent: [
        {
          type: 'DATA',
          consentString: 'Data Consent String',
          status: consentData.DATA,
          dateTimeUTC: new Date(),
        },

        {
          type: 'MARKETING',
          consentString: 'Marketing Consent String',
          status: consentData.MARKETING,
          dateTimeUTC: new Date(),
        },

        {
          type: 'ANALYTICS',
          consentString: 'Analytics Consent String',
          status: consentData.ANALYTICS,
          dateTimeUTC: new Date(),
        },
      ],
    };

    const response = await this.signupService.signup(account);
    const firebaseToken = response.token;
    const userCredential = await this.firebaseService.signInWithCustomToken(
      firebaseToken
    );
    await this.firebaseService.sendVerificationEmail();
    await this.userService.refreshMe();

    if (this.currentInviteToken) {
      const inviteRoute = 'accept-invite/' + this.currentInviteToken;
      this.router.navigate([inviteRoute]);
      return;
    }

    this.router.navigate(['/home']);
    this.message.showSuccess('Welcome', 'Successfully signed in');
  }
}
